import { c as _c } from "react/compiler-runtime";
// Контекст обучающих туров. Держит активный тур и его шаг, отдаёт методы
// запуска/навигации и запоминает пройденные туры в localStorage, чтобы не
// приставать повторно. Кнопка «?» перезапускает тур в любой момент — «просмотр»
// влияет только на автоподсказку первого визита.
import { isTourTargetVisible } from "@osmix/ui-primitives/tour-layout";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { GuidedTour } from "./guided-tour";
import { type TourDef, type TourStep, TOURS } from "./tours";
const STORAGE_KEY = "osmix.admin.tour.seen";
const readSeen = (): Set<string> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter(x => typeof x === "string")) : new Set();
  } catch {
    return new Set();
  }
};
const writeSeen = (seen: Set<string>) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...seen]));
  } catch {
    // приватный режим / заблокированное хранилище — просто не запоминаем
  }
};
type TourContextValue = {
  /** Запустить тур с первого шага. */
  start: (key: string) => void;
  /** Уже пройден ли тур (для ненавязчивой автоподсказки). */
  isSeen: (key: string) => boolean;
};
const TourContext = createContext<TourContextValue | null>(null);
export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error("useTour must be used within TourProvider");
  }
  return ctx;
}
export function TourProvider(t0) {
  const $ = _c(25);
  const {
    children
  } = t0;
  const location = useLocation();
  const [active, setActive] = useState(null);
  const [step, setStep] = useState(0);
  const [seen, setSeen] = useState(readSeen);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = key => {
      setSeen(prev => {
        if (prev.has(key)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(key);
        writeSeen(next);
        return next;
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const markSeen = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = key_0 => {
      const tour = TOURS.find(item => item.key === key_0);
      if (!tour) {
        return;
      }
      const visible = tour.steps.filter(_temp);
      setActive({
        tour,
        steps: visible.length > 0 ? visible : tour.steps.slice(0, 1)
      });
      setStep(0);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const start = t2;
  let t3;
  if ($[2] !== active) {
    t3 = () => {
      if (active) {
        markSeen(active.tour.key);
      }
      setActive(null);
      setStep(0);
    };
    $[2] = active;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const stop = t3;
  let t4;
  if ($[4] !== active || $[5] !== step || $[6] !== stop) {
    t4 = () => {
      if (!active) {
        return;
      }
      if (step >= active.steps.length - 1) {
        stop();
        return;
      }
      setStep(step + 1);
    };
    $[4] = active;
    $[5] = step;
    $[6] = stop;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const next_0 = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setStep(_temp2);
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const prev_0 = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setActive(null);
      setStep(0);
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== location.pathname) {
    t7 = [location.pathname];
    $[10] = location.pathname;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== seen) {
    t8 = key_1 => seen.has(key_1);
    $[12] = seen;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const isSeen = t8;
  let t9;
  if ($[14] !== isSeen) {
    t9 = {
      start,
      isSeen
    };
    $[14] = isSeen;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const value = t9;
  let t10;
  if ($[16] !== active || $[17] !== next_0 || $[18] !== step || $[19] !== stop) {
    t10 = active && <GuidedTour steps={active.steps} step={step} onNext={next_0} onPrev={prev_0} onClose={stop} />;
    $[16] = active;
    $[17] = next_0;
    $[18] = step;
    $[19] = stop;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== children || $[22] !== t10 || $[23] !== value) {
    t11 = <TourContext.Provider value={value}>{children}{t10}</TourContext.Provider>;
    $[21] = children;
    $[22] = t10;
    $[23] = value;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}
function _temp2(current) {
  return Math.max(0, current - 1);
}
function _temp(item_0) {
  return isTourTargetVisible(item_0.target);
}